import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FaStar, FaTimes } from "react-icons/fa";
import CommonService from "../../services/common.service";
import {
  formatMetric,
  getCapacity,
  getCertificationShortLabels,
  getCompletedOrders,
  getIndustryTags,
  getManufacturerLocation,
  getMoqSummary,
  getRatingDisplay,
  getResponseTime,
} from "../../utils/contractManufacturerCardHelpers";

const EMPTY_DIMENSION_UNIT_OPTIONS = [];
const EMPTY_INDUSTRY_FILTER_OPTIONS = [];

function CompareRating({ item }) {
  const rating = getRatingDisplay(item);
  if (rating == null) return <span className="marketplace-compare-modal__muted">—</span>;

  const numericRating = Number(rating);
  return (
    <div className="marketplace-compare-modal__rating">
      <span className="marketplace-compare-modal__stars" aria-hidden>
        {Array.from({ length: 5 }).map((_, index) => (
          <FaStar key={index} className={index + 1 <= Math.round(numericRating) ? "is-filled" : ""} />
        ))}
      </span>
      <span className="marketplace-compare-modal__rating-value">{rating}</span>
    </div>
  );
}

function CompareCertifications({ item }) {
  const badges = getCertificationShortLabels(item, 8);
  if (!badges.length) return <span className="marketplace-compare-modal__muted">—</span>;

  return (
    <div className="marketplace-compare-modal__cert-list">
      {badges.map((badge) => (
        <span key={badge} className="marketplace-compare-modal__cert">
          {badge}
        </span>
      ))}
    </div>
  );
}

function buildCompareRows(dimensionUnitOptions, industryFilterOptions) {
  return [
    { key: "location", label: "Location", getValue: (item) => formatMetric(getManufacturerLocation(item)) },
    { key: "capacity", label: "Capacity", getValue: (item) => formatMetric(getCapacity(item)) },
    { key: "response", label: "Response", getValue: (item) => formatMetric(getResponseTime(item)) },
    { key: "orders", label: "Orders", getValue: (item) => formatMetric(getCompletedOrders(item)) },
    {
      key: "moq",
      label: "MOQ",
      getValue: (item) => formatMetric(getMoqSummary(item, dimensionUnitOptions)),
    },
    { key: "rating", label: "Rating", render: (item) => <CompareRating item={item} /> },
    {
      key: "industries",
      label: "Industries",
      getValue: (item) => {
        const tags = getIndustryTags(item, industryFilterOptions, 4);
        return tags.length ? tags.join(", ") : "—";
      },
    },
    { key: "certifications", label: "Certifications", render: (item) => <CompareCertifications item={item} /> },
  ];
}

export default function ContractManufacturerComparisonModal({
  open,
  manufacturers = [],
  dimensionUnitOptions: dimensionUnitOptionsProp,
  industryFilterOptions = EMPTY_INDUSTRY_FILTER_OPTIONS,
  onClose,
}) {
  const [fetchedDimensionUnitOptions, setFetchedDimensionUnitOptions] = useState(EMPTY_DIMENSION_UNIT_OPTIONS);

  const dimensionUnitOptions =
    dimensionUnitOptionsProp?.length > 0 ? dimensionUnitOptionsProp : fetchedDimensionUnitOptions;

  useEffect(() => {
    if (!open || dimensionUnitOptions.length > 0) return undefined;
    let cancelled = false;
    CommonService.get("/master/product-catalogue-details")
      .then((res) => {
        if (cancelled) return;
        const rows = res?.data?.master_diamension_unit || [];
        setFetchedDimensionUnitOptions(
          rows
            .map((du) => ({
              value: du.dimention_unit_id ?? du.dimension_unit_id ?? du.unit_id ?? du.id ?? du.value,
              label: du.dimention_unit ?? du.dimension_unit ?? du.unit_name ?? du.unit ?? du.label ?? du.name,
            }))
            .filter((opt) => opt.value != null && opt.label != null && String(opt.label).trim() !== ""),
        );
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [open, dimensionUnitOptions.length]);

  const compareRows = buildCompareRows(dimensionUnitOptions, industryFilterOptions);

  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open, onClose]);

  if (!open || manufacturers.length < 2) return null;

  return (
    <div className="marketplace-compare-modal-backdrop" onClick={onClose} role="presentation">
      <div
        className="marketplace-compare-modal"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="cm-compare-modal-title"
      >
        <header className="marketplace-compare-modal__header">
          <h2 id="cm-compare-modal-title">Manufacturer Comparison</h2>
          <button type="button" className="marketplace-compare-modal__close" onClick={onClose} aria-label="Close">
            <FaTimes aria-hidden />
          </button>
        </header>

        <div className="marketplace-compare-modal__body">
          <table className="marketplace-compare-modal__table">
            <thead>
              <tr>
                <th className="marketplace-compare-modal__attr-head">Attribute</th>
                {manufacturers.map((item) => {
                  const isVerified = String(item?.verification_status || "").toLowerCase() === "verified";
                  return (
                    <th key={item.company_id} className="marketplace-compare-modal__product-head">
                      <div className="marketplace-compare-modal__product-title">{item.company_name || "Manufacturer"}</div>
                      <div className="marketplace-compare-modal__product-company">Contract Manufacturer</div>
                      {isVerified ? <span className="marketplace-compare-modal__verified">✓ VERIFIED</span> : null}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {compareRows.map((row) => (
                <tr key={row.key} className="marketplace-compare-modal__row">
                  <td className="marketplace-compare-modal__attr-cell">{row.label}</td>
                  {manufacturers.map((item) => (
                    <td key={`${row.key}-${item.company_id}`} className="marketplace-compare-modal__value-cell">
                      {row.render ? row.render(item) : row.getValue(item)}
                    </td>
                  ))}
                </tr>
              ))}
              <tr className="marketplace-compare-modal__row marketplace-compare-modal__row--actions">
                <td className="marketplace-compare-modal__attr-cell" />
                {manufacturers.map((item) => (
                  <td key={`action-${item.company_id}`} className="marketplace-compare-modal__value-cell">
                    <Link
                      to={`/contract-manufacturers/rfq/${item.company_id}`}
                      className="marketplace-compare-modal__quote-btn"
                    >
                      Request Quote
                    </Link>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
